import { useMemo, useState } from "react";

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(String(value).replace(/[$,]/g, ""));
    if (isNaN(num)) return "";
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const FieldInput = ({
    id,
    value,
    readOnly,
    type = "text",
    alignRight,
    onChange,
    ...rest
}) => {
    return (
        <input
            id={`field-${id}`}
            type={type}
            value={value ?? ""}
            readOnly={!!readOnly}
            onChange={onChange}
            className={`form-input w-full ${alignRight ? "text-right" : ""
                } ${readOnly ? "cursor-not-allowed bg-gray-100" : ""}`}
            aria-label={id}
            {...rest}
        />
    );
};



const Row = ({ children }) => (
    <div
        className="grid gap-6 items-center py-2"
        style={{ gridTemplateColumns: "140px 240px 40px 140px 240px" }}
    >
        {children}
    </div>
);


const Label = ({ text }) => <div className="form-label-13 block text-sm pr-2">{text}</div>;

export default function LenderCreditYspControls({
    fieldValues,
    setFieldValues,
    loanAmountId,
    premiumPctId,
    premiumAmtId,
    brokerYspId,
    computed
}) {
    const {
        borrowerCredit,
        lenderCreditPct,
        lenderCreditAmt,
        yspToBrokerPct,
        yspToBrokerAmt
    } = computed;

    const formatDecimal3 = (val) => {
        const num = Number(val);
        if (!Number.isFinite(num)) return "";
        return num.toFixed(3);
    };

    const [brokerTouched, setBrokerTouched] = useState(false);

    const brokerYspUiValue = useMemo(() => {
        const yspRaw = fieldValues[brokerYspId];
        const yspNum = Number(yspRaw);

        // if user already started typing, never auto-fallback again
        if (brokerTouched) return yspRaw ?? "";

        // fallback if empty OR 0
       const isEmpty = yspRaw === "" || yspRaw === null || yspRaw === undefined;
        const isZero = Number.isFinite(yspNum) && yspNum === 0;

        if (isEmpty || isZero) {
            return fieldValues["CX.BROKER.YSP.PCT"] ?? "";
        }

        return yspRaw ?? "";
    }, [fieldValues, brokerYspId, brokerTouched]);

    const onBrokerYspChange = (e) => {
        const raw = e.target.value;
        setBrokerTouched(true);

        // allow empty
        if (raw === "") {
            setFieldValues(prev => ({ ...prev, [brokerYspId]: "" }));
            return;
        }

        // allow typing states, max 3 decimals
        const ok = /^(\d+)?(\.\d{0,3})?$/.test(raw);
        if (!ok) return;

        const premiumMax = Number(fieldValues[premiumPctId] ?? 0);

        // enforce max while typing (only if it parses)
        const num = Number(raw);
        if (Number.isFinite(num) && Number.isFinite(premiumMax) && num > premiumMax) {
            setFieldValues(prev => ({ ...prev, [brokerYspId]: premiumMax.toFixed(3) }));
            return;
        }

        setFieldValues(prev => ({ ...prev, [brokerYspId]: raw }));
    };


    const onBrokerYspBlur = () => {
        setBrokerTouched(true);

        const raw = fieldValues[brokerYspId];
        if (raw === "" || raw == null) return;

        const num = Number(raw);
        if (!Number.isFinite(num)) return;

        const premiumMax = Number(fieldValues[premiumPctId] ?? 0);
        const clamped = Number.isFinite(premiumMax) ? Math.min(num, premiumMax) : num;

        setFieldValues(prev => ({ ...prev, [brokerYspId]: clamped.toFixed(3) }));
    };



    return (
        <>
            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                <div className="section-header px-4 py-2">Broker YSP & Lender Credit</div>

                <div className="px-4 py-4 space-y-2">
                    {/* Optional: show Loan Amount (hidden in screenshot but helpful for debugging) */}
                    <Row style={{ opacity: 0.7 }}>
                        <Label text="Loan Amount" />
                        <FieldInput
                            id={loanAmountId}
                            value={formatCurrency(fieldValues[loanAmountId]) || (fieldValues[loanAmountId] ?? "")}
                            readOnly
                            type="text"
                            alignRight
                        />
                        <div className="col-span-1" />

                        <Label text="Broker YSP" />
                        <FieldInput
                            id={brokerYspId}
                            value={brokerYspUiValue}
                            type="text"
                            alignRight
                            onChange={onBrokerYspChange}
                            onBlur={onBrokerYspBlur}
                            inputMode="decimal"
                            placeholder="0.000"
                        />

                    </Row>
                    {/* Row 1: Premium (readonly) */}
                    <Row>
                        <Label text="Premium Percentage" />
                        <FieldInput
                            id={premiumPctId}
                            value={fieldValues[premiumPctId] ?? ""}
                            readOnly
                            type="text"
                            alignRight
                        />
                        <div className="col-span-1" />

                        <Label text="Lender Credit" />
                        <FieldInput
                            id="UI.BorrowerCredit"
                            value={formatDecimal3(borrowerCredit)}
                            readOnly
                            alignRight
                        />
                    </Row>

                    <Row>
                        <Label text="Premium Amount" />
                        <FieldInput
                            id={premiumAmtId}
                            value={formatCurrency(fieldValues[premiumAmtId]) || (fieldValues[premiumAmtId] ?? "")}
                            readOnly
                            type="text"
                            alignRight
                        />
                        <div className="col-span-3" />
                    </Row>

                    {/* Lender Credit % + Lender Credit Amount */}
                    <Row>
                        <Label text="Lender Credit" />
                        <FieldInput
                            id="UI.LenderCreditPct"
                            value={formatDecimal3(lenderCreditPct)}
                            readOnly
                            alignRight
                        />

                        <div className="col-span-3" />
                    </Row>

                    <Row>
                        <Label text="Lender Credit Amount" />
                        <FieldInput
                            id="UI.LenderCreditAmt"
                            value={formatCurrency(lenderCreditAmt)}
                            readOnly
                            alignRight
                        />

                        <div className="col-span-3" />
                    </Row>

                    {/* YSP to Broker % + YSP to Broker Amount */}
                    <Row>
                        <Label text="YSP to Broker" />
                        <FieldInput
                            id="UI.YspToBrokerPct"
                            value={String(yspToBrokerPct ?? 0)}
                            readOnly
                            alignRight
                        />

                        <div className="col-span-3" />

                    </Row>

                    <Row>
                        <Label text="YSP to Broker Amount" />
                        <FieldInput
                            id="UI.YspToBrokerAmt"
                            value={formatCurrency(yspToBrokerAmt)}
                            readOnly
                            alignRight
                        />

                        <div className="col-span-3" />
                    </Row>



                </div>
            </div>
        </>
    );
}
